import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Sparkles, CheckCircle2 } from "lucide-react";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "Exclusieve seizoenscollecties & shoot-specials",
  "Vroege toegang tot minishoots en kortingen",
  "Persoonlijke fotografietips van Eric",
];

const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !consent) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        name: name.trim() || undefined,
        source: "home-newsletter",
      });
      setSubscribed(true);
      toast.success("Bedankt — jouw inschrijving is bevestigd.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Er ging iets mis. Probeer opnieuw.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid={HOME.newsletterSection}
      className="relative bg-beige py-28 md:py-40 overflow-hidden"
    >
      {/* Decorative gold rule */}
      <div className="absolute left-1/2 -translate-x-1/2 top-0 w-px h-16 bg-gold/40" />

      <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
        {/* Left — copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="md:col-span-6"
        >
          <p className="eyebrow mb-6 flex items-center gap-2">
            <Sparkles size={14} className="text-gold" /> Nieuwsbrief
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Blijf op de hoogte van <span className="italic text-gold">nieuwe collecties.</span>
          </h2>
          <p className="mt-8 text-ink/70 max-w-lg leading-relaxed">
            Schrijf je in voor persoonlijke updates van Lenslopers. Geen spam — enkel de mooiste
            momenten, exclusieve aanbiedingen en seizoensspecials, direct in jouw inbox.
          </p>

          <ul className="mt-10 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-ink/80">
                <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Right — form card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="md:col-span-6"
        >
          <div className="relative bg-white shadow-[0_30px_80px_-40px_rgba(17,17,17,0.35)] border border-ink/5">
            {/* Gold corner accents */}
            <span className="absolute top-0 left-0 w-8 h-px bg-gold" />
            <span className="absolute top-0 left-0 h-8 w-px bg-gold" />
            <span className="absolute bottom-0 right-0 w-8 h-px bg-gold" />
            <span className="absolute bottom-0 right-0 h-8 w-px bg-gold" />

            <div className="p-10 md:p-14">
              {subscribed ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 mx-auto flex items-center justify-center">
                    <CheckCircle2 size={30} className="text-gold" />
                  </div>
                  <h3 className="font-serif text-3xl mt-6 text-ink">Welkom aan boord</h3>
                  <p className="mt-4 text-ink/70 leading-relaxed">
                    Bedankt {name ? name.split(" ")[0] : ""} — jouw inschrijving is bevestigd.
                    Je hoort binnenkort van me.
                  </p>
                  <p className="mt-8 font-script text-2xl text-gold">Eric</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6">
                  <div className="flex items-center gap-3 text-ink/60 text-xs uppercase tracking-[0.3em]">
                    <Mail size={14} className="text-gold" />
                    <span>Schrijf je in</span>
                  </div>

                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.25em] text-ink/50 mb-2">
                      Naam <span className="normal-case text-ink/40">(optioneel)</span>
                    </label>
                    <input
                      data-testid={HOME.newsletterName}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Voornaam"
                      className="w-full bg-transparent border-b border-ink/20 focus:border-gold px-0 py-3 outline-none text-ink placeholder-ink/30 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.25em] text-ink/50 mb-2">
                      E-mailadres
                    </label>
                    <input
                      data-testid={HOME.newsletterInput}
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="je@email.be"
                      className="w-full bg-transparent border-b border-ink/20 focus:border-gold px-0 py-3 outline-none text-ink placeholder-ink/30 transition-colors"
                    />
                  </div>

                  <label className="flex items-start gap-3 text-xs text-ink/60 leading-relaxed cursor-pointer select-none pt-2">
                    <input
                      data-testid={HOME.newsletterConsent}
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#C89B5C] cursor-pointer"
                    />
                    <span>
                      Ja, ik ontvang graag persoonlijke updates van Lenslopers en ga akkoord dat
                      mijn gegevens conform de privacyverklaring worden verwerkt. Uitschrijven kan
                      op elk moment.
                    </span>
                  </label>

                  <button
                    data-testid={HOME.newsletterSubmit}
                    type="submit"
                    disabled={loading || !consent || !email}
                    className="btn-gold w-full py-4 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Bezig met inschrijven..." : "Schrijf mij in"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsletterSection;
